/** @prettier */

/**
 * 文字列変換関連関数一覧
 */

import {TFunction} from 'i18next';
import {roundOff as roundOffForNumber} from './convertNumber';
import {monthNameType} from '../Type/TypeDef';

/**
 * APIからのサニタイズされた文字をもとに戻す
 * @param value サニタイズされた文字
 * @returns 戻された文字
 */
const sanitizeRestore = (value: string) => {
  //&amp; → &    &lt; → <     &gt → >
  //&quot; → "   &#x27; → '   &#x60; → `   &#x2F; → /

  return value
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'")
    .replace(/&#x60;/g, '`')
    .replace(/&#x2F;/g, '/');
};

/**
 * サニタイズされたオブジェクトをもとに戻す
 * @param value サニタイズされたオブジェクト
 * @returns 戻されたオブジェクト
 */
const sanitizeRestoreObject = (value: object) => {
  //prettier-ignore
  return (Object.entries(value) as Array<[string, number | string | (string | object)[] | object]>).reduce((result, [key, data]) => {
    if (Array.isArray(data)) {
      // 配列の場合
      result[key] = (data as Array<string | object | Array<string | object>>).map(arrayData => {
        if (Array.isArray(arrayData)) {
          // 二次元配列の場合
          return arrayData.map(innerData => {
            if (typeof innerData === 'object') {
              return sanitizeRestoreObject(innerData);
            } else {
              return sanitizeRestore(innerData);
            }
          });
        } else if (typeof arrayData === 'object') {
          return sanitizeRestoreObject(arrayData);
        } else {
          return sanitizeRestore(arrayData);
        }
      });
    }
    else if (typeof data === 'object') {
      //オブジェクトの場合
      result[key] = sanitizeRestoreObject(data);
    } else if (typeof data === 'string') {
      //文字列の場合
      result[key] = sanitizeRestore(data);
    } else if (typeof data === 'number') {
      //数値の場合
      result[key] = sanitizeRestore(String(data));
    }
    return result;
  }, {} as {[key: string]: any});
};

/**
 * 文字の先頭を大文字、後ろを小文字に変換
 * @param value 変換する文字列
 * @returns 変換された文字列
 */
const initialUpper = (value: string) => {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

/**
 * ピリオドをカンマに変換
 * @param value ピリオドが含まれた文字
 * @returns カンマに変換された文字
 */
const invertComma = (value: string) => {
  return value.replace('.', ',');
};

/**
 * ピリオド区切りを行う
 * @param value 区切りたい文字
 * @returns ピリオド区切りした文字
 */
const numberWithPeriod = (value: string) => {
  return value.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1.');
};

/**
 * 文字列の数値を四捨五入を行う
 * @param value 四捨五入する文字列の数値
 * @param num 小数点num位まで残す
 * @returns 四捨五入した値
 */
const roundOff = (value: string, num: number = 0) => {
  try {
    return roundOffForNumber(Number(value), num).toFixed(num);
  } catch (error) {
    return (0.0).toFixed(num);
  }
};

/**
 * 月名を翻訳して表示する
 * @param month 翻訳する月名
 * @param trans 翻訳関数
 * @returns 翻訳した月名
 */
const monthTranslation = (month: monthNameType, trans: TFunction) => {
  //月名を翻訳して返す
  return trans(`convertString.monthTranslation.${month}`);
};

/**
 * 月名を翻訳して表示する(省略形)
 * @param month 翻訳する月名
 * @param trans 翻訳関数
 * @returns 翻訳した月名
 */
const monthAbbreviationTranslation = (
  month: monthNameType,
  trans: TFunction
) => {
  //月名を翻訳して返す
  return trans(`convertString.monthTranslation.abbreviation.${month}`);
};

export {
  sanitizeRestore,
  sanitizeRestoreObject,
  initialUpper,
  invertComma,
  numberWithPeriod,
  roundOff,
  monthTranslation,
  monthAbbreviationTranslation,
};
